import { useMemo, useState } from 'react';

import { ValueBar } from './FuelsSustainabilityShared';
import { formatSig } from './gasLaws';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { pathwayTarget, pathwayTitle, type PathwayCase } from './synthesisDesign';
import type { GreenComparison } from './synthesisDesignRoutes';
import { ExploreChallenges } from './ExploreChallenges';
import {
  DEFAULT_GREEN_SETUP,
  GREEN_CHALLENGES,
  GREEN_EXPLORE_FIELDS,
  GREEN_QUESTION,
  calculateGreenExplore,
  type GreenExploreInput,
} from './synthesisGreenCalc';
import {
  YIELD_CHALLENGES,
  YIELD_QUESTION,
  calculateYieldExplore,
  defaultYieldValues,
  yieldExploreFields,
} from './synthesisYieldCalc';

export type SynthesisView = 'explore' | 'practice';

const VIEW_LABELS: Record<SynthesisView, string> = { explore: 'Explore', practice: 'Practice' };

/** Explore (own inputs, working always visible) or Practice (answer first, working after Check). */
export function SynthesisViewToggle({ view, onChange }: { view: SynthesisView; onChange: (next: SynthesisView) => void }) {
  return (
    <div className="inline-grid grid-cols-2 rounded-full border border-hairline bg-surface p-1" role="group" aria-label="Choose a view">
      {(Object.keys(VIEW_LABELS) as SynthesisView[]).map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={view === option}
          onClick={() => onChange(option)}
          className={`rounded-full px-4 py-1.5 text-sm font-extrabold ${view === option ? 'bg-ink text-white' : 'text-slate2 hover:bg-white'}`}
        >
          {VIEW_LABELS[option]}
        </button>
      ))}
    </div>
  );
}

function safely<T>(compute: () => T): { value: T | null; error: string | null } {
  try {
    return { value: compute(), error: null };
  } catch (error) {
    return { value: null, error: error instanceof Error ? error.message : 'This setup cannot be calculated.' };
  }
}

/** Yield explorer for one route: starting mass and every step yield typed by the learner. Remount per route. */
export function SynthesisYieldExplore({ entry }: { entry: PathwayCase }) {
  const fields = useMemo(() => yieldExploreFields(entry), [entry]);
  const [values, setValues] = useState<Record<string, number>>(() => defaultYieldValues(entry));
  const { value: result, error } = useMemo(() => safely(() => calculateYieldExplore(entry, values)), [entry, values]);

  return (
    <div className="mt-4 grid gap-4">
      <p className="text-sm font-medium leading-6 text-slate2">
        Route: <strong className="text-ink">{pathwayTitle(entry)}</strong>. Each step works on the product of the step
        before, so the yields multiply. The starting values are practice yields; change them to see the effect.
      </p>
      <ModelInputPanel
        legend="Your route"
        fields={fields}
        values={values}
        onChange={setValues}
        sliderKey={fields[fields.length - 1].key}
      />
      <ExploreChallenges question={YIELD_QUESTION} challenges={YIELD_CHALLENGES} result={result} />
      {result ? (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <WorkedStepsPanel
            title="How the model works it out"
            steps={result.worked}
            stepTestIdPrefix="yield-step"
            result={`${formatSig(result.finalMassG, 3)} g of ${pathwayTarget(entry).name} · overall yield ${formatSig(result.overallPercent, 3)} %`}
            resultTestId="yield-explore-result"
            limitNote="Model: 1 mol of product per mol of the previous compound at every step, and each step uses all of the previous product."
          />
          <figure className="min-w-0 rounded-2xl border border-hairline bg-canvas-pure p-4" aria-label="Product after each step">
            <figcaption className="text-sm font-extrabold text-ink">Product left after each step</figcaption>
            <div className="mt-3 grid gap-1.5">
              <ValueBar label={`Start: ${result.startName}`} value={100} max={100} valueText="100%" highlight={false} tone="sky" />
              {result.steps.map((item, index) => (
                <ValueBar
                  key={`${item.name}-${index}`}
                  label={`Step ${index + 1}: ${item.name}`}
                  value={item.cumulativePercent}
                  max={100}
                  valueText={`${formatSig(item.cumulativePercent, 3)}%`}
                  highlight={index === result.steps.length - 1}
                  tone="mint"
                />
              ))}
            </div>
            <ul className="mt-3 grid gap-1 text-xs font-bold text-slate2">
              {result.steps.map((item, index) => (
                <li key={`${item.name}-mass-${index}`} data-testid={`yield-mass-${index + 1}`}>
                  Step {index + 1}: {formatSig(item.moles, 3)} mol = {formatSig(item.massG, 3)} g {item.name}
                </li>
              ))}
            </ul>
          </figure>
        </div>
      ) : (
        <p role="alert" className="rounded-2xl bg-wash-coral p-4 text-sm font-bold text-ink">
          {error}
        </p>
      )}
    </div>
  );
}

/** Atom economy of both routes with the molar masses substituted, for the learner's reactant mass. */
export function SynthesisGreenExplore({ entry }: { entry: GreenComparison }) {
  const [values, setValues] = useState<Record<GreenExploreInput, number>>(DEFAULT_GREEN_SETUP);
  const { value: routes, error } = useMemo(() => safely(() => calculateGreenExplore(entry, values)), [entry, values]);

  return (
    <div className="mt-4 grid gap-4">
      <ModelInputPanel legend="Your batch" fields={GREEN_EXPLORE_FIELDS} values={values} onChange={setValues} sliderKey="reactantMassG" />
      <ExploreChallenges question={GREEN_QUESTION} challenges={GREEN_CHALLENGES} result={routes} />
      {routes ? (
        <>
          <div className="grid gap-4 xl:grid-cols-2">
            {(['A', 'B'] as const).map((key) => (
              <div key={key} className="min-w-0">
                <p className="mb-2 break-words font-mono text-sm font-bold text-ink">
                  Route {key}: {routes[key].equation}
                </p>
                <WorkedStepsPanel
                  title={`Route ${key}: ${routes[key].name}`}
                  steps={routes[key].steps}
                  stepTestIdPrefix="green-step"
                  result={`AE ${formatSig(routes[key].percent, 3)} % · ${formatSig(routes[key].desiredMassG, 3)} g ${routes[key].desiredFormula}, ${formatSig(routes[key].byProductMassG, 3)} g by-products`}
                  resultTestId={`green-result-${key.toLowerCase()}`}
                />
              </div>
            ))}
          </div>
          <figure className="min-w-0 rounded-2xl border border-hairline bg-surface p-4" aria-label="Desired product from your reactant mass">
            <figcaption className="text-sm font-extrabold text-ink">
              Desired product from {formatSig(values.reactantMassG, 3)} g of reactants (100 % yield)
            </figcaption>
            <div className="mt-3 grid gap-1.5">
              {(['A', 'B'] as const).map((key) => (
                <ValueBar
                  key={key}
                  label={`Route ${key}: ${routes[key].name}`}
                  value={routes[key].desiredMassG}
                  max={values.reactantMassG}
                  valueText={`${formatSig(routes[key].desiredMassG, 3)} g`}
                  highlight={routes[key].percent >= routes[key === 'A' ? 'B' : 'A'].percent}
                  tone="mint"
                />
              ))}
            </div>
          </figure>
        </>
      ) : (
        <p role="alert" className="rounded-2xl bg-wash-coral p-4 text-sm font-bold text-ink">
          {error}
        </p>
      )}
    </div>
  );
}
